import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const lastUpdated = 'March 2026';

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/login');
    }
  };

  const S = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="mb-8">
      <h2 className="text-xl font-semibold text-clinic-dark font-body mb-4">{title}</h2>
      {children}
    </section>
  );
  const P = ({ children }: { children: React.ReactNode }) => <p className="text-clinic-muted mb-4">{children}</p>;
  const H3 = ({ children }: { children: React.ReactNode }) => <h3 className="text-lg font-medium text-clinic-dark mt-4 mb-2">{children}</h3>;
  const UL = ({ children }: { children: React.ReactNode }) => <ul className="list-disc list-inside text-clinic-muted space-y-1 mb-4">{children}</ul>;

  return (
    <div className="min-h-screen bg-clinic-surface">
      <header className="bg-white shadow-clinic-sm border-b border-clinic-primary-lighter">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={handleBack} className="inline-flex items-center text-clinic-muted hover:text-clinic-primary transition-colors">
              <ArrowLeftIcon className="h-5 w-5 mr-2" />Back
            </button>
            <img src="/logos/logo-primary.png" alt="Clinic Studios" className="h-8 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-card shadow-clinic p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-clinic-primary/20 rounded-full flex items-center justify-center mr-4">
              <ShieldCheckIcon className="h-6 w-6 text-clinic-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-clinic-dark font-body">Privacy Policy</h1>
              <p className="text-sm text-clinic-muted">Last updated: {lastUpdated}</p>
            </div>
          </div>

          <div className="prose max-w-none text-clinic-dark">
            <P>Clinic Studios ("we," "our," or "us") is committed to protecting personal information in accordance with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation. This Privacy Policy explains how we collect, use, disclose, and safeguard personal information when you use our website, booking platform, and physical facilities.</P>

            <S title="1. Nature of Our Services">
              <P>Clinic Studios provides professional workspace and administrative booking tools for independent mental health and allied professionals ("Members"). Clinic Studios does not provide healthcare or professional services, does not act as a clinic of record, does not provide electronic health record services, and does not collect or store clinical records, diagnostic information, or treatment notes.</P>
              <P>All professional services are provided solely by independent Members. Members are responsible for complying with all applicable professional and health privacy legislation relating to their services.</P>
            </S>

            <S title="2. Information We Collect">
              <H3>A. Client Account Information</H3>
              <P>When a Client creates an account, we collect:</P>
              <UL>
                <li>First name and last name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Account password (stored in encrypted form)</li>
              </UL>
              <P>When a Client books an appointment, we collect the selected Member, appointment date and time, and booking status.</P>
              <P>We do not collect health history, clinical notes, diagnostic information, intake forms, insurance information, or payment information for professional services.</P>
              <div className="bg-clinic-cream/50 rounded-card p-4 border border-clinic-primary-lighter mb-4">
                <p className="text-clinic-dark"><strong>Note:</strong> Although we do not collect clinical records, we treat booking information as sensitive because it relates to professional service appointments.</p>
              </div>

              <H3>B. Member Information</H3>
              <P>From Members, we collect name and contact information, professional credentials required for membership eligibility, profile information provided voluntarily for directory visibility, availability and booking data, and billing and membership payment information.</P>
              <P>Members are responsible for ensuring that the information they provide is accurate and lawful.</P>

              <H3>C. Technical and Usage Information</H3>
              <P>We automatically collect certain technical information, including IP address, browser and device type, log-in timestamps, and platform usage activity. We use this information for security monitoring, fraud prevention, system performance, and analytics and service improvement.</P>

              <H3>D. Analytics and Cookies</H3>
              <P>We use analytics tools and cookies to understand how users interact with our website and platform. These tools may collect pages visited, time spent on pages, referring websites, and device and browser information. Some analytics providers may process data in Canada or the United States. You may control cookie preferences through your browser settings.</P>

              <H3>E. Video Monitoring</H3>
              <P>For safety and operational purposes, certain non-clinical areas of Clinic Studios facilities are monitored by video. Clinical meeting rooms are not monitored. No audio recording is conducted. Video footage is stored securely in cloud systems and retained for up to one (1) year, after which it is securely deleted unless required for legal or security investigations.</P>
            </S>

            <S title="3. How We Use Personal Information">
              <P>We use personal information to:</P>
              <UL>
                <li>Create and manage user accounts</li>
                <li>Facilitate appointment bookings</li>
                <li>Provide access control to facilities</li>
                <li>Administer Member billing and room usage</li>
                <li>Send booking confirmations and administrative communications</li>
                <li>Maintain platform security</li>
                <li>Improve website and platform functionality</li>
                <li>Comply with legal obligations</li>
              </UL>
              <P>We do not sell personal information. We do not use booking data for advertising of unrelated third-party products.</P>
            </S>

            <S title="4. Cross-Border Data Transfers">
              <P>Clinic Studios uses service providers located in Canada and the United States. As a result, personal information may be stored or processed in either country. Where information is processed outside of Canada, it may be subject to the laws of that jurisdiction. We use contractual and technical safeguards to protect personal information regardless of where it is processed.</P>
            </S>

            <S title="5. Disclosure of Information">
              <P>We may disclose personal information to the selected Member for appointment scheduling purposes, to service providers who assist in operating our platform (for example, hosting providers, payment processors, and analytics providers), to legal authorities where required by law, and to protect the safety, rights, or property of Clinic Studios or others.</P>
              <P>We do not disclose clinical information because we do not collect it.</P>
            </S>

            <S title="6. Data Retention">
              <UL>
                <li>Client booking history and transactional records are retained for legal and audit purposes even if an account is deleted</li>
                <li>Inactive account information may be deactivated or deleted upon request, subject to retention requirements</li>
                <li>Video footage is retained for up to one (1) year unless required for investigation</li>
                <li>Technical logs are retained for a limited period necessary for security monitoring</li>
              </UL>
              <P>When information is no longer required, it is securely deleted or anonymized.</P>
            </S>

            <S title="7. Account Deletion">
              <P>Users may request deletion of their account. Upon deletion, active account access will be removed. Certain booking and transactional records may be retained for legal and audit purposes. Backup systems may retain residual copies for a limited period consistent with standard data recovery practices.</P>
            </S>

            <S title="8. Data Security">
              <P>We implement reasonable technical and organizational safeguards appropriate to the sensitivity of the information, including encryption of data in transit, secure password storage, role-based access controls, secure hosting environments, and monitoring for unauthorized access.</P>
              <P>While no system can guarantee absolute security, we take reasonable steps to protect personal information.</P>
            </S>

            <S title="9. Breach Notification">
              <P>In the event of a breach involving a real risk of significant harm, we will notify affected individuals and the Office of the Privacy Commissioner of Canada as required by law.</P>
            </S>

            <S title="10. Your Rights">
              <P>Under applicable privacy laws, you may have the right to:</P>
              <UL>
                <li>Request access to your personal information</li>
                <li>Request correction of inaccurate information</li>
                <li>Withdraw consent where applicable</li>
                <li>File a complaint with the Office of the Privacy Commissioner of Canada</li>
              </UL>
              <P>Requests may be submitted to our Privacy Officer.</P>
            </S>

            <S title="11. Children and Minors">
              <P>Clinic Studios accounts are intended for individuals who are legally capable of entering into binding agreements. Where bookings involve minors, Members are responsible for obtaining appropriate consent in accordance with applicable laws.</P>
            </S>

            <S title="12. Changes to This Policy">
              <P>We may update this Privacy Policy from time to time. Material changes will be communicated via email or platform notification. Continued use of the platform after updates take effect constitutes acceptance of the revised Policy.</P>
            </S>

            <S title="13. Contact Information">
              <P>Privacy Officer:</P>
              <div className="bg-clinic-cream/50 rounded-card p-4 border border-clinic-primary-lighter">
                <p className="text-clinic-dark">
                  <strong>Clinic Studios</strong><br />
                  Email: info@clinicstudios.ca<br />
                  Phone: (403) 471-8617<br />
                  Address: 30 Springborough Blvd SW Suite 214, Calgary, AB T3H 5M6
                </p>
              </div>
            </S>

            <section className="border-t border-clinic-primary-lighter pt-6">
              <p className="text-sm text-clinic-muted">
                Your privacy matters to us. If you have questions or concerns about how we handle your
                personal information, please don't hesitate to contact our Privacy Officer.
              </p>
            </section>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-clinic-primary-lighter mt-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <img src="/logos/logo-icon.png" alt="Clinic Studios" className="h-8 w-auto opacity-60" />
              <p className="ml-4 text-sm text-clinic-muted">&copy; {new Date().getFullYear()} Clinic Studios. All rights reserved.</p>
            </div>
            <div className="flex gap-4">
              <Link to="/privacy" className="text-sm text-clinic-primary font-medium">Privacy Policy</Link>
              <Link to="/terms" className="text-sm text-clinic-muted hover:text-clinic-primary transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
